import * as React from 'react';
import { Icon } from './icons.jsx';
import { TONE_BG } from './data';

// Shared UI primitives.

const uiStyles = {
  btnBase: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    border: '1px solid transparent', borderRadius: 999,
    fontWeight: 600, fontSize: 14, lineHeight: 1,
    padding: '12px 20px', cursor: 'pointer',
    transition: 'transform .08s ease, background .15s ease, border-color .15s ease, box-shadow .15s ease',
    userSelect: 'none', whiteSpace: 'nowrap',
  },
};

export const Button = ({ variant = 'primary', size = 'md', icon, iconRight, children, style, onClick, type = 'button', disabled, full, className = '', ...rest }) => {
  const sizes = {
    sm: { padding: '8px 14px', fontSize: 13 },
    md: { padding: '12px 20px', fontSize: 14 },
    lg: { padding: '15px 24px', fontSize: 15 },
    xl: { padding: '17px 28px', fontSize: 16 },
  };
  const variants = {
    primary: {
      background: 'linear-gradient(180deg, #2E7AF0 0%, var(--brand) 60%, #1557BF 100%)', color: '#fff',
      boxShadow: '0 1px 0 rgba(255,255,255,0.20) inset, 0 14px 28px -18px rgba(26,102,213,0.88)',
    },
    secondary: {
      background: 'var(--surface)', color: 'var(--ink)',
      borderColor: 'var(--line-2)',
      boxShadow: '0 1px 0 rgba(255,255,255,0.90) inset, var(--shadow-sm)',
    },
    ghost: {
      background: 'transparent', color: 'var(--ink-2)',
    },
    quiet: {
      background: 'var(--bg-2)', color: 'var(--ink-2)',
    },
    danger: {
      background: '#FFF', color: '#A03A2A', borderColor: '#E9C9C0',
    },
    coral: {
      background: 'var(--coral)', color: '#fff',
    },
    yellow: {
      background: 'linear-gradient(180deg, #FFD85A 0%, var(--yellow) 100%)', color: 'var(--ink)',
      boxShadow: '0 1px 0 rgba(255,255,255,0.5) inset, 0 12px 24px -18px rgba(244,196,67,0.85)',
    },
  };
  const merged = {
    ...uiStyles.btnBase, ...sizes[size], ...variants[variant],
    opacity: disabled ? 0.5 : 1,
    pointerEvents: disabled ? 'none' : 'auto',
    width: full ? '100%' : undefined,
    ...style,
  };
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={className}
      style={merged}
      onMouseDown={(e) => { e.currentTarget.style.transform = 'translateY(1px)'; }}
      onMouseUp={(e)   => { e.currentTarget.style.transform = ''; }}
      onMouseLeave={(e)=> { e.currentTarget.style.transform = ''; }}
      {...rest}
    >
      {icon && <Icon name={icon} size={size === 'sm' ? 14 : 16} />}
      <span>{children}</span>
      {iconRight && <Icon name={iconRight} size={size === 'sm' ? 14 : 16} />}
    </button>
  );
};

export const Badge = ({ tone = 'neutral', icon, children, style }) => {
  const tones = {
    neutral: { background: 'var(--surface)', color: 'var(--ink-2)', borderColor: 'var(--line)' },
    open:    { background: 'var(--open-soft)', color: 'var(--open)',  borderColor: 'transparent' },
    wait:    { background: 'var(--wait-soft)', color: 'var(--wait)',  borderColor: 'transparent' },
    brand:   { background: 'var(--brand-soft)', color: 'var(--brand-ink)', borderColor: 'transparent' },
    coral:   { background: 'var(--coral-soft)', color: '#9B1F1A', borderColor: 'transparent' },
    yellow:  { background: 'var(--yellow)', color: 'var(--ink)', borderColor: 'transparent' },
    ink:     { background: 'var(--ink)', color: 'var(--bg)', borderColor: 'transparent' },
  };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '4px 10px', borderRadius: 999,
      fontSize: 12, fontWeight: 600, lineHeight: 1.5,
      border: '1px solid', whiteSpace: 'nowrap',
      ...tones[tone], ...style,
    }}>
      {icon === 'live' ? <><span className="live-dot" aria-hidden="true" /><span className="sr-only">Live availability.</span></> : icon ? <Icon name={icon} size={12}/> : null}
      {children}
    </span>
  );
};

export const Tag = ({ children, style }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center',
    padding: '3px 9px', borderRadius: 6,
    fontSize: 12, fontWeight: 500,
    background: 'var(--bg-2)', color: 'var(--ink-2)',
    ...style,
  }}>{children}</span>
);

export const Card = ({ children, style, hoverable, onClick }) => {
  const [hov, setHov] = React.useState(false);
  const [tilt, setTilt] = React.useState({ x: 0, y: 0 });
  const reducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const updateTilt = (e) => {
    if (!hoverable || reducedMotion()) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    setTilt({ x: py * -3, y: px * 3 });
  };
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => hoverable && setHov(true)}
      onMouseMove={updateTilt}
      onMouseLeave={() => { if (hoverable) { setHov(false); setTilt({ x: 0, y: 0 }); } }}
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--line)',
        borderRadius: 'var(--r-lg)',
        boxShadow: hoverable && hov ? 'var(--shadow-md)' : 'var(--shadow-sm)',
        transform: hoverable && hov ? `perspective(800px) translateY(-2px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)` : 'none',
        transformStyle: 'preserve-3d',
        transition: 'transform .18s ease, box-shadow .18s ease, border-color .18s ease',
        borderColor: hoverable && hov ? 'var(--line-2)' : 'var(--line)',
        cursor: onClick ? 'pointer' : 'default',
        ...style,
      }}
    >
      {children}
    </div>
  );
};

const PLACEHOLDER_ICONS = {
  A: 'home',
  B: 'logo-leaf',
  C: 'star',
  D: 'shield',
  E: 'home',
  F: 'star',
};

export const Placeholder = ({ label = 'photo', tone = 'A', radius = 14, style, height, className = '', iconSize }) => {
  const isHero = className.includes('profile-hero') || Number(height) >= 300;
  return (
    <div className={`ph ${className}`} style={{
      width: '100%', height: height || '100%',
      borderRadius: radius,
      backgroundImage: TONE_BG[tone] || TONE_BG.A,
      ...style,
    }}>
      <span className="ph-icon" aria-hidden="true">
        <Icon name={PLACEHOLDER_ICONS[tone] || 'home'} size={iconSize || (isHero ? 44 : 28)} strokeWidth={1.8}/>
      </span>
      {label && <span className="ph-label">{label}</span>}
    </div>
  );
};

export const ProviderPhoto = ({ src, alt = '', label, radius = 14, style, height, className = '', imgStyle, loading = 'lazy', objectPosition = 'center' }) => (
  <div className={`provider-photo ${className}`} style={{
    width: '100%', height: height || '100%',
    borderRadius: radius,
    overflow: 'hidden',
    position: 'relative',
    background: 'var(--bg-2)',
    boxShadow: 'inset 0 0 0 1px rgba(14,42,74,0.10)',
    ...style,
  }}>
    <img
      src={src}
      alt={alt}
      loading={loading}
      style={{
        width: '100%', height: '100%', display: 'block',
        objectFit: 'cover', objectPosition,
        ...imgStyle,
      }}
    />
    {label && <span className="ph-label">{label}</span>}
  </div>
);

export const Field = ({ id, label, hint, error, children, style }) => {
  const generatedId = React.useId().replace(/:/g, '');
  const fieldId = id || generatedId;
  const control = React.isValidElement(children)
    ? React.cloneElement(children, { id: children.props.id || fieldId })
    : children;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, ...style }}>
      {label && <label htmlFor={fieldId} style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-2)' }}>{label}</label>}
      {control}
      {hint && !error && <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>{hint}</span>}
      {error && <span style={{ fontSize: 12, color: '#A03A2A' }}>{error}</span>}
    </div>
  );
};

export const Input = React.forwardRef(({ icon, style, ...props }, ref) => {
  const [focus, setFocus] = React.useState(false);
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      background: 'var(--surface)',
      border: `1px solid ${focus ? 'var(--brand)' : 'var(--line-2)'}`,
      boxShadow: focus ? '0 0 0 3px rgba(26,102,213,0.14)' : 'none',
      borderRadius: 12, padding: '12px 14px',
      transition: 'border-color .15s ease, box-shadow .15s ease',
      width: '100%', minWidth: 0, maxWidth: '100%',
      ...style,
    }}>
      {icon && <Icon name={icon} size={16} style={{ color: 'var(--ink-3)' }}/>}
      <input
        ref={ref}
        {...props}
        onFocus={(e) => { setFocus(true); props.onFocus && props.onFocus(e); }}
        onBlur={(e)  => { setFocus(false); props.onBlur && props.onBlur(e); }}
        style={{
          flex: 1, border: 'none', outline: 'none', background: 'transparent',
          fontSize: 15, color: 'var(--ink)', minWidth: 0,
        }}
      />
    </div>
  );
});

export const Select = ({ icon, value, onChange, options, placeholder, style, ...props }) => {
  const [focus, setFocus] = React.useState(false);
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      background: 'var(--surface)',
      border: `1px solid ${focus ? 'var(--brand)' : 'var(--line-2)'}`,
      boxShadow: focus ? '0 0 0 3px rgba(26,102,213,0.14)' : 'none',
      borderRadius: 12, padding: '12px 14px',
      transition: 'border-color .15s ease, box-shadow .15s ease',
      position: 'relative',
      ...style,
    }}>
      {icon && <Icon name={icon} size={16} style={{ color: 'var(--ink-3)' }}/>}
      <select
        {...props}
        value={value} onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
        style={{
          flex: 1, border: 'none', outline: 'none', background: 'transparent',
          fontSize: 15, color: value ? 'var(--ink)' : 'var(--ink-3)',
          appearance: 'none', paddingRight: 20, cursor: 'pointer',
        }}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options.map(o => <option key={o.value || o} value={o.value || o}>{o.label || o}</option>)}
      </select>
      <Icon name="chev-down" size={16} style={{ color: 'var(--ink-3)', marginLeft: -28, pointerEvents: 'none' }}/>
    </div>
  );
};

export const Toggle = ({ value, onChange, label }) => (
  <button
    type="button"
    onClick={() => onChange(!value)}
    style={{
      display: 'inline-flex', alignItems: 'center', gap: 10,
      background: value ? 'var(--brand-soft)' : 'var(--surface)',
      border: `1px solid ${value ? 'var(--brand)' : 'var(--line-2)'}`,
      color: value ? 'var(--brand-ink)' : 'var(--ink-2)',
      borderRadius: 999, padding: '8px 14px 8px 10px',
      cursor: 'pointer', fontSize: 13, fontWeight: 600,
      transition: 'all .15s ease',
    }}
  >
    <span style={{
      width: 32, height: 18, borderRadius: 999,
      background: value ? 'var(--brand)' : '#D6CDBC',
      position: 'relative', transition: 'background .15s ease',
    }}>
      <span style={{
        position: 'absolute', top: 2, left: value ? 16 : 2,
        width: 14, height: 14, borderRadius: 999, background: '#fff',
        transition: 'left .15s ease', boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
      }}/>
    </span>
    {label}
  </button>
);

export const Pill = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      padding: '8px 14px', borderRadius: 999,
      background: active ? 'var(--ink)' : 'var(--surface)',
      color: active ? '#fff' : 'var(--ink-2)',
      border: `1px solid ${active ? 'var(--ink)' : 'var(--line-2)'}`,
      fontSize: 13, fontWeight: 600, cursor: 'pointer',
      transition: 'all .12s ease',
    }}
  >{children}</button>
);

export const ProgressBar = ({ value, label }) => (
  <div>
    {label && (
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-2)' }}>{label}</span>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--brand)' }}>{value}%</span>
      </div>
    )}
    <div style={{ height: 8, borderRadius: 999, background: 'var(--bg-2)', overflow: 'hidden' }}>
      <div style={{
        width: `${value}%`, height: '100%', borderRadius: 999,
        background: 'var(--brand)', transition: 'width .4s ease',
      }}/>
    </div>
  </div>
);

export const Modal = ({ open, onClose, children, width = 560, ariaLabel = 'Dialog' }) => {
  const cardRef = React.useRef(null);
  const lastFocusRef = React.useRef(null);
  React.useEffect(() => {
    if (!open) return;
    lastFocusRef.current = document.activeElement;
    const focusableSelector = 'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';
    const focusFirst = () => {
      const focusables = cardRef.current ? Array.from(cardRef.current.querySelectorAll(focusableSelector)) : [];
      (focusables[0] || cardRef.current)?.focus();
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !cardRef.current) return;
      const focusables = Array.from(cardRef.current.querySelectorAll(focusableSelector));
      if (!focusables.length) {
        e.preventDefault();
        cardRef.current.focus();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    window.setTimeout(focusFirst, 0);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
      lastFocusRef.current?.focus?.();
    };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div
      className="modal-overlay"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(30,25,20,0.42)',
        display: 'grid', placeItems: 'center',
        padding: '40px 16px',
        backdropFilter: 'blur(4px)',
      }}
    >
      <div
        ref={cardRef}
        className="modal-card"
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        tabIndex="-1"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface)',
          borderRadius: 'var(--r-xl)',
          width: '100%', maxWidth: width,
          boxShadow: 'var(--shadow-lg)',
          maxHeight: 'calc(100vh - 80px)',
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
        }}
      >{children}</div>
    </div>
  );
};
